import { Box, Button, InputBase, Tooltip, Typography } from '@mui/material';
import { ChangeEvent, FC, useMemo } from 'react';
import styled from 'styled-components';
import { NumberFormatCustom } from '../form-elements/NumberInput';

const NAMES_SHOWN = 3;

const AvailableInput = styled(InputBase)`
  & .MuiInputBase-input {
    width: 130px;
    text-align: center;
    padding: 12px 0;
    height: 1.43em;
    background: white;
    border: 1px solid #2fd60a;
    border-radius: 4px;

    &:focus::placeholder {
      opacity: 0;
    }
  }
`;

interface IPointsRow {
  id: number;
  availablePoints: number;
  earnedPoints: number;
  user?: { firstName?: string; lastName?: string; email?: string };
}

interface IBulkAvailablePointsFormProps {
  rows: IPointsRow[];
  masterPoints: number;
  value: string;
  onChange: (value: string) => void;
  onApply: (value: number) => void;
  onCommit: (value: number | null) => void;
  loading?: boolean;
}

const userLabel = (row: IPointsRow) =>
  [row.user?.firstName, row.user?.lastName].filter(Boolean).join(' ') ||
  row.user?.email ||
  `#${row.id}`;

const format = (value: number) => value.toLocaleString('en');

const earnedOf = (row: IPointsRow) => Number(row.earnedPoints);

const earnedLabel = (row: IPointsRow) =>
  `${userLabel(row)} (Earned ${format(earnedOf(row))})`;

interface IBulkAvailableValidation {
  isValid: boolean;
  newValue: number;
  errorLines: string[];
}

// Same rules as the Available cell and the bulk endpoint, checked against all rows
const validateBulkAvailable = (
  value: string,
  rows: IPointsRow[],
  masterPoints: number
): IBulkAvailableValidation => {
  const newValue = Number(value);
  const isValid =
    value.trim() !== '' && Number.isInteger(newValue) && newValue >= 0;
  const result = (errorLines: string[]) => ({ isValid, newValue, errorLines });

  if (!value) return result([]);
  if (!isValid) return result(['Enter a whole number, 0 or more']);

  const usersCount = rows.length;
  if (usersCount === 0) {
    return result(['There are no users in this company']);
  }

  const sumAvailable = rows.reduce(
    (acc, row) => acc + Number(row.availablePoints),
    0
  );
  const delta = newValue * usersCount - sumAvailable;
  // Lowest value allowed by Earned and highest value Master + current Available can cover
  const minValue = Math.max(0, ...rows.map(earnedOf));
  const maxValue = Math.floor((masterPoints + sumAvailable) / usersCount);

  if (minValue > maxValue) {
    const needed = minValue * usersCount - sumAvailable - masterPoints;
    return result([
      `Not enough Master Points to set the same value for all users: setting the minimum value ${format(
        minValue
      )} needs ${format(needed)} more Master Points`,
    ]);
  }

  const belowEarned = rows.filter((row) => earnedOf(row) > newValue);
  if (belowEarned.length) {
    const names = belowEarned.slice(0, NAMES_SHOWN).map(earnedLabel).join(', ');
    const hiddenCount = belowEarned.length - NAMES_SHOWN;
    const more = hiddenCount > 0 ? ` and ${hiddenCount} more` : '';
    return result([
      `Value must be greater than users already earned points for ${belowEarned.length} user(s): ${names}${more}`,
      `Min value you can set: ${format(minValue)}`,
    ]);
  }

  if (delta > masterPoints) {
    // The pool is Master plus the Available taken back from every user
    const totalNeeded = newValue * usersCount;
    const totalAvailable = masterPoints + sumAvailable;

    return result([
      `Additional Master Points Required: ${format(
        totalNeeded - totalAvailable
      )}`,
      `${format(totalNeeded)} needed, ${format(totalAvailable)} available`,
      `Max value you can set: ${format(maxValue)}`,
    ]);
  }

  return result([]);
};

const BulkAvailablePointsForm: FC<IBulkAvailablePointsFormProps> = ({
  rows,
  masterPoints,
  value,
  onChange,
  onApply,
  onCommit,
  loading = false,
}) => {
  const { isValid, newValue, errorLines } = useMemo(
    () => validateBulkAvailable(value, rows, Number(masterPoints)),
    [value, rows, masterPoints]
  );

  // While rows load the user list is unknown, so no validation message is shown
  const hasErrors = !loading && errorLines.length > 0;
  const canApply = !loading && isValid && !hasErrors;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  // Like the Available cell: on blur a valid value is committed (Master Points
  // are recalculated), an invalid one is dropped.
  const handleBlur = () => {
    if (hasErrors) {
      onChange('');
    }
    onCommit(canApply ? newValue : null);
  };

  const handleApply = () => {
    onApply(newValue);
  };

  const tooltipTitle = errorLines.map((line) => <div key={line}>{line}</div>);

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Typography
        component="label"
        htmlFor="bulk-available-points"
        variant="body2"
        color="text.secondary"
      >
        Set Available for all users
      </Typography>
      <Box display="flex" alignItems="center" gap={2}>
        <Tooltip open={hasErrors} title={tooltipTitle}>
          <Box>
            <AvailableInput
              id="bulk-available-points"
              placeholder="Available Points"
              value={value}
              onChange={handleChange}
              onBlur={handleBlur}
              inputComponent={NumberFormatCustom as any}
              disabled={loading}
            />
          </Box>
        </Tooltip>
        <Button color="secondary" disabled={!canApply} onClick={handleApply}>
          Apply
        </Button>
      </Box>
    </Box>
  );
};

export default BulkAvailablePointsForm;
